import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AppHeader } from '@/components/AppHeader';
import { BottomTabs } from '@/components/BottomTabs';
import { DateStrip } from '@/components/DateStrip';
import { DataCredits } from '@/components/DataCredits';
import { ConfirmSheet } from '@/components/ConfirmSheet';
import { DayEditSheet } from '@/components/DayEditSheet';
import { EditedBy } from '@/components/EditedBy';
import { MenuSheet } from '@/components/MenuSheet';
import { OptimizeSheet } from '@/components/OptimizeSheet';
import { TransportChip } from '@/components/TransportChip';
import {
  ChatIcon,
  AlertIcon,
  BusIcon,
  ClockIcon,
  FerryIcon,
  GripIcon,
  HelpIcon,
  LeaveIcon,
  ListIcon,
  PencilIcon,
  PinIcon,
  PlaneIcon,
  PlusIcon,
  ReorderIcon,
  RouteIcon,
  ShareIcon,
  TrainIcon,
  TrashIcon,
  UsersIcon,
  CalendarIcon,
  WalletIcon,
  IdeaIcon,
} from '@/components/icons';
import { useDayLegs, type LegInfo } from '@/hooks/useDayLegs';
import { useDayLodging } from '@/hooks/useDayLodging';
import { useInviteShare } from '@/hooks/useInviteShare';
import { useReorderDrag } from '@/hooks/useReorderDrag';
import { buildIcs } from '@/domain/ics';
import { changedSince, markSeen, readSeen } from '@/data/seen';
import { formatDayText } from '@/domain/dayText';
import { timeConflicts, timeSortedOrder } from '@/domain/order';
import { useMinuteTick } from '@/hooks/useMinuteTick';
import { useSwipe } from '@/hooks/useSwipe';
import {
  formatDateLabel,
  formatMinutes,
  formatOffsetDelta,
  timezoneShift,
  tzShortLabel,
} from '@/domain/time';
import { slackMinutes } from '@/domain/schedule';
import { currentItem, endTimeOf, findToday } from '@/domain/today';
import { zoneLabel } from '@/domain/timezones';
import { isSegmentKind, type Item } from '@/domain/types';
import { useLocale, useT } from '@/i18n';
import { Rich } from '@/i18n/Rich';
import { platform } from '@/platform';
import { defaultDateFor } from '@/domain/today';
import { useTripStore } from '@/store/tripStore';

/** 구간 종류별 칩 색. 수단이 다르면 한눈에 갈려야 한다. */
const KIND_TONE: Record<Item['kind'], string> = {
  place: '',
  flight: 'transit',
  train: 'accent',
  bus: 'car',
  ferry: 'walk',
};

function KindIcon({ kind }: { kind: Item['kind'] }) {
  if (kind === 'flight') return <PlaneIcon />;
  if (kind === 'train') return <TrainIcon />;
  if (kind === 'bus') return <BusIcon />;
  if (kind === 'ferry') return <FerryIcon />;
  return null;
}

/** 구간 한 줄. 상태별로 다르게 보여준다 — 특히 '모름'을 숨기지 않는다. */
/** 늦으면 몇 분(양수), 아니면 undefined */
function lateByOf(prev: Item, next: Item, travel: number | undefined): number | undefined {
  const slack = slackMinutes(prev, next, travel);
  return slack !== null && slack < 0 ? -slack : undefined;
}

/** lateBy: 앞 일정을 마치고 이동하면 다음 일정에 몇 분 늦는지(늦지 않으면 없음) */
function LegRow({ info, lateBy }: { info?: LegInfo; lateBy?: number }) {
  const t = useT();
  const locale = useLocale();
  return (
    <div className="tl-leg">
      <div className="tl-leg__rail">
        <div className="tl-leg__line" />
      </div>
      <div className="tl-leg__body">
        {!info && null}

        {info?.status === 'loading' && <span className="tl-leg__skel" />}

        {info?.status === 'manual' && info.mode && (
          <TransportChip mode={info.mode} minutes={info.minutes} manual />
        )}

        {info?.status === 'ready' && info.mode && (
          <>
            <TransportChip mode={info.mode} minutes={info.minutes} />
            {info.transitMissing && (
              <span className="chip chip--warn">
                <AlertIcon size={11} />
                {t.leg.transitMissing}
              </span>
            )}
          </>
        )}

        {info?.status === 'cross_border' && (
          <span className="chip chip--warn">
            <AlertIcon size={11} />
            {t.leg.crossBorder}
          </span>
        )}

        {info?.status === 'unavailable' && (
          <span className="chip">{info.missingPlace ? t.leg.needsPlace : t.leg.unknownTap}</span>
        )}
        {/* 늦는 구간 — 여행 당일이 아니라 계획할 때 알게 */}
        {lateBy !== undefined && (
          <span className="chip chip--warn">
            <AlertIcon size={11} />
            {t.leg.late(formatMinutes(lateBy, locale))}
          </span>
        )}
      </div>
    </div>
  );
}

export function TripScreen() {
  const { tripId = '' } = useParams();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  /** 순서 바꾸기 모드. 켜면 손잡이가 나오고 카드를 눌러도 상세로 가지 않는다. */
  const [reorder, setReorder] = useState(false);
  const [optimizeOpen, setOptimizeOpen] = useState(false);
  const moveItem = useTripStore((s) => s.moveItem);
  const setDayOrder = useTripStore((s) => s.setDayOrder);
  const ideaCount = useTripStore((s) => s.places.filter((p) => p.tripId === tripId).length);
  const checklist = useTripStore((s) => s.checklist);
  const expenseCount = useTripStore((s) => s.expenses.filter((e) => e.tripId === tripId).length);
  const packing = checklist.filter((c) => c.tripId === tripId);
  const packed = packing.filter((c) => c.checked).length;
  const allComments = useTripStore((s) => s.comments);
  /** 일정별 댓글 수 — 타임라인 카드에 💬 N */
  const commentCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of allComments) m.set(c.itemId, (m.get(c.itemId) ?? 0) + 1);
    return m;
  }, [allComments]);
  const [dayEditOpen, setDayEditOpen] = useState(false);
  const [confirm, setConfirm] = useState<'leave' | 'delete' | null>(null);
  const me = useTripStore((s) => s.currentUserId);
  const leaveTrip = useTripStore((s) => s.leaveTrip);
  const deleteTrip = useTripStore((s) => s.deleteTrip);
  const { share: shareInvite, toast } = useInviteShare();
  /** 하루 일정 보내기의 결과(복사됨 등) */
  const [dayToast, setDayToast] = useState<string | null>(null);
  const dayToastTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(dayToastTimer.current), []);
  const t = useT();
  const locale = useLocale();

  const trip = useTripStore((s) => s.getTrip(tripId));
  const allItems = useTripStore((s) => s.items);

  /*
   * 지난번에 이 여행을 본 때. 그 뒤에 친구가 바꾼 일정에 "새로"를 단다(data/seen.ts).
   * 열 때 읽어 두고 지금으로 적는다 — 다음에 열면 이번 방문 이후 것만 표시된다.
   */
  const [seenAt, setSeenAt] = useState<string | null>(null);
  useEffect(() => {
    if (!me || !tripId) return;
    setSeenAt(readSeen(me, tripId));
    markSeen(me, tripId);
  }, [me, tripId]);

  // 날짜가 주소에 없으면 여행 중엔 오늘, 아니면 첫날
  const activeDate = search.get('date') ?? (trip ? defaultDateFor(trip) : '');
  const dayIndex = trip?.days.findIndex((d) => d.date === activeDate) ?? -1;
  const day = dayIndex >= 0 ? trip?.days[dayIndex] : undefined;
  const prevDay = dayIndex > 0 ? trip?.days[dayIndex - 1] : undefined;

  const items = useMemo(
    () =>
      allItems
        .filter((i) => i.tripId === tripId && i.date === activeDate)
        .sort((a, b) => (a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0)),
    [allItems, tripId, activeDate],
  );

  // 숙소 — 전날 묵은 곳에서 출발해 이 날 묵는 곳으로 끝난다(hooks/useDayLodging.ts)
  const { start: lodgingStart, end: lodgingEnd, routeItems: legItems } = useDayLodging(
    trip,
    activeDate,
    items,
  );
  const legs = useDayLegs(legItems, day?.timezone);
  /** 앞 일정보다 이른 시각인 일정. 순서를 바꾸거나 다른 날에서 옮겨 오면 생긴다. */
  const conflicts = useMemo(() => timeConflicts(items.map((i) => i.localTime)), [items]);

  /*
   * 여행 당일이면 "지금"을 보여 준다 — 하고 있는 일정에 표시, 지나간 일정은 흐리게.
   * 오늘인지는 그 날 도시의 현지 날짜로 판단한다(domain/today.ts).
   */
  const tick = useMinuteTick();
  const nowLocal = useMemo(() => {
    if (!trip) return null;
    const today = findToday([trip], tick);
    return today && today.day.date === activeDate ? today.localTime : null;
  }, [trip, tick, activeDate]);
  const nowItem = nowLocal ? currentItem(items, nowLocal) : null;
  const isPast = (item: Item): boolean => {
    if (!nowLocal || item === nowItem || !item.localTime) return false;
    return (endTimeOf(item) ?? item.localTime) <= nowLocal;
  };
  const hasConflict = conflicts.some(Boolean);

  function goToDay(offset: number) {
    if (!trip || dayIndex < 0) return;
    const next = trip.days[dayIndex + offset];
    if (!next) return;
    setSearch({ date: next.date }, { replace: true });
    platform.vibrate(8);
  }

  // 스케치의 "슬라이드 시 해당 일정의 날짜 변동"
  const swipe = useSwipe({
    onSwipeLeft: () => goToDay(1),
    onSwipeRight: () => goToDay(-1),
  });

  const reorderDrag = useReorderDrag((from, to) => {
    moveItem(tripId, activeDate, from, to);
    platform.vibrate(8);
  });

  /**
   * 이 날 일정을 글로 만들어 공유 시트로 — 카카오톡을 고르면 단톡방에 바로 간다.
   * 공유 시트가 없는 곳(데스크톱)은 복사하고 알린다.
   */
  async function onShareDay() {
    if (!trip || !day) return;
    setMenuOpen(false);
    const city = day.cityLabel || zoneLabel(day.timezone, locale);
    const text = formatDayText({
      heading: t.shareDay.heading(
        trip.coverEmoji,
        trip.name,
        dayIndex + 1,
        formatDateLabel(day.date, locale),
        city,
      ),
      items,
      lodging: day.lodgingName ? t.trip.lodgingAt(day.lodgingName) : undefined,
      legOf: (it) => {
        const leg = legs.get(it.id);
        return leg?.mode && leg.minutes !== undefined
          ? { mode: leg.mode, minutes: leg.minutes }
          : undefined;
      },
      labels: {
        minutes: (n) => formatMinutes(n, locale),
        mode: (m) => t.transport[m],
        empty: t.shareDay.empty,
      },
    });
    const result = await platform.share({ title: trip.name, text });
    if (result === 'shared' || result === 'cancelled') return;
    setDayToast(result === 'copied' ? t.shareDay.copied : t.shareDay.failed);
    clearTimeout(dayToastTimer.current);
    dayToastTimer.current = setTimeout(() => setDayToast(null), 2400);
  }

  async function onShare() {
    if (!trip) return;
    setMenuOpen(false);
    await shareInvite(trip);
  }

  if (!trip || !day) {
    return (
      <div className="app">
        <AppHeader title={t.trip.title} back />
        <main className="main">
          {/* 보고 있던 여행이 지워지거나 내보내졌을 때도 여기로 온다 */}
          <p className="empty">{t.trip.notFound}</p>
        </main>
        <BottomTabs />
      </div>
    );
  }

  const shift = timezoneShift(prevDay, day);
  const cityName = day.cityLabel || zoneLabel(day.timezone, locale);
  const isOwner = trip.ownerId === me;
  const others = trip.members.filter((m) => m.id !== me).length;

  return (
    <div className="app">
      {/* 순서 바꾸기 중에는 메뉴 대신 '완료' — 둘은 같은 자리를 쓴다 */}
      {reorder ? (
        <AppHeader
          title={trip.name}
          back
          action={{ label: t.trip.reorderDone, onClick: () => setReorder(false) }}
        />
      ) : (
        <AppHeader title={trip.name} back onMenu={() => setMenuOpen(true)} />
      )}

      <DateStrip
        days={trip.days}
        activeDate={activeDate}
        onSelect={(date) => setSearch({ date }, { replace: true })}
      />

      {/* 가로 스와이프로 날짜 전환. 세로 스크롤은 그대로 동작한다. */}
      {/* 순서 바꾸기 중에는 끌기와 헷갈리지 않게 날짜 스와이프를 끈다 */}
      <main className="main" {...(reorder ? {} : swipe)} style={{ touchAction: 'pan-y' }}>
        {/*
          메뉴 속에 숨어 있던 자주 쓰는 기능을 상태와 함께 바로 보이게 한다.
          여행 중에 "돈 누가 냈지", "뭐 챙겼지"를 보려고 메뉴를 열 필요가 없다.
          메뉴에도 그대로 남아 있다.
        */}
        {!reorder && (
          <nav className="shortcuts" aria-label={t.trip.shortcuts}>
            <button
              className="shortcut"
              onClick={() => navigate(`/trip/${trip.id}/checklist`)}
            >
              <ListIcon size={15} />
              {t.trip.shortcutChecklist(packed, packing.length)}
            </button>
            <button
              className="shortcut"
              onClick={() => navigate(`/trip/${trip.id}/expenses?date=${activeDate}`)}
            >
              <WalletIcon size={15} />
              {t.trip.shortcutLedger(expenseCount)}
            </button>
            <button
              className="shortcut"
              onClick={() => navigate(`/trip/${trip.id}/ideas?date=${activeDate}`)}
            >
              <IdeaIcon size={15} />
              {t.trip.shortcutIdeas(ideaCount)}
            </button>
          </nav>
        )}

        {/*
          도시·타임존은 머리를 눌러 고친다. 도시를 옮기는 날만 고치는 값이라
          화면에 따로 버튼을 두기보다 그 값이 보이는 자리를 누르게 한다.
        */}
        <button className="dayhead dayhead--edit" onClick={() => setDayEditOpen(true)}>
          <div className="dayhead__row">
            <span className="dayhead__city">{cityName}</span>
            <span className="chip">{tzShortLabel(day.timezone, day.date)}</span>
            <PencilIcon size={14} className="dayhead__pencil" />
          </div>
          <div className="dayhead__date">
            {t.common.dayWithDate(dayIndex + 1, formatDateLabel(day.date, locale))}
          </div>
        </button>

        {/* 타임존이 바뀌는 날 경고. 전세계 여행에서 제일 조용히 터지는 부분. */}
        {shift.changed && (
          <div className="section" style={{ paddingTop: 10, paddingBottom: 0 }}>
            <div className="banner">
              <AlertIcon className="banner__icon" />
              <div>
                <Rich
                  text={t.trip.tzBanner(formatOffsetDelta(shift.deltaMinutes, locale), cityName)}
                />
              </div>
            </div>
          </div>
        )}

        {reorder && (
          <div className="section" style={{ paddingTop: 10, paddingBottom: 0 }}>
            <div className="banner banner--info">
              <ReorderIcon className="banner__icon" />
              <div>{t.trip.reorderHint}</div>
            </div>
          </div>
        )}

        <div className={`timeline${reorder ? ' timeline--reorder' : ''}`}>
          {items.length === 0 && <p className="empty">{t.trip.emptyDay}</p>}

          {reorder &&
            items.map((item, i) => (
              <div
                key={item.id}
                ref={reorderDrag.rowRef(i)}
                style={reorderDrag.rowStyle(i)}
                className={`tl-row${reorderDrag.drag?.from === i ? ' tl-row--dragging' : ''}`}
              >
                <div className="tl-time">{item.localTime ?? '—'}</div>
                <div className="tl-item tl-item--reorder">
                  <div className="tl-item__head">
                    <span className="tl-item__title">{item.title}</span>
                    <button
                      className="tl-grip"
                      aria-label={t.trip.reorderHandle(item.title)}
                      {...reorderDrag.handleProps(i, items.length)}
                    >
                      <GripIcon />
                    </button>
                  </div>
                  {item.placeName && <div className="tl-item__place">{item.placeName}</div>}
                </div>
              </div>
            ))}

          {/*
            시각과 순서가 어긋난 날. 자정을 넘기는 일정일 수도 있어 고치라고 강요하지
            않는다 — 알려주고, 원하면 한 번에 정렬하게 한다.
          */}
          {!reorder && hasConflict && (
            <div className="banner timeline__notice">
              <AlertIcon className="banner__icon" />
              <div>
                {t.trip.timeConflict}
                <button
                  className="banner__action"
                  onClick={() => {
                    setDayOrder(trip.id, activeDate, timeSortedOrder(items));
                    platform.vibrate(8);
                  }}
                >
                  {t.trip.sortByTime}
                </button>
              </div>
            </div>
          )}

          {!reorder && prevDay?.lodgingName && (
            <div className="tl-lodging">
              <span aria-hidden>🏨</span>
              <span className="tl-lodging__text">{t.trip.lodgingFrom(prevDay.lodgingName)}</span>
            </div>
          )}

          {!reorder && items.map((item, i) => (
            <div key={item.id}>
              {/* 첫 일정 앞은 전날 숙소에서 오는 길 */}
              {i === 0 && lodgingStart && <LegRow info={legs.get(item.id)} />}
              {i > 0 && (
                <LegRow
                  info={legs.get(item.id)}
                  lateBy={lateByOf(items[i - 1]!, item, legs.get(item.id)?.minutes)}
                />
              )}

              <div className="tl-row">
                <div
                  className={`tl-time${conflicts[i] ? ' tl-time--conflict' : ''}`}
                  title={conflicts[i] ? t.trip.timeConflictShort : undefined}
                >
                  {conflicts[i] && <AlertIcon size={11} className="tl-time__warn" />}
                  {item.localTime ?? '—'}
                  {item.durationMin !== undefined && (
                    <span className="tl-time__dur">{formatMinutes(item.durationMin, locale)}</span>
                  )}
                </div>

                <button
                  className={`tl-item${item === nowItem ? ' tl-item--now' : ''}${isPast(item) ? ' tl-item--past' : ''}`}
                  onClick={() => navigate(`/trip/${trip.id}/item/${item.id}`)}
                >
                  <div className="tl-item__head">
                    {item === nowItem && (
                      <span className="chip chip--accent now-chip">
                        <span className="now-dot" aria-hidden /> {t.today.now}
                      </span>
                    )}
                    <span className="tl-item__title">{item.title}</span>
                    {changedSince(item, me, seenAt) && (
                      <span
                        className="chip chip--new"
                        aria-label={t.trip.newBadgeAria(
                          trip.members.find((m) => m.id === item.updatedBy)?.name ?? t.edited.leftMember,
                        )}
                      >
                        {t.trip.newBadge}
                      </span>
                    )}
                    {isSegmentKind(item.kind) && (
                      <span className={`chip chip--${KIND_TONE[item.kind]}`}>
                        <KindIcon kind={item.kind} />
                        {item.carrierCode ?? t.kind[item.kind]}
                      </span>
                    )}
                    <EditedBy item={item} members={trip.members} variant="avatar" />
                  </div>

                  {item.placeName && (
                    <div className="tl-item__place">
                      {item.kind === 'place' ? <PinIcon /> : <ClockIcon />}
                      {item.placeName}
                    </div>
                  )}

                  {item.description && <div className="tl-item__desc">{item.description}</div>}
                  {(commentCount.get(item.id) ?? 0) > 0 && (
                    <div className="tl-item__comments">
                      <span aria-hidden>💬</span> {t.comments.count(commentCount.get(item.id)!)}
                    </div>
                  )}
                </button>
              </div>
            </div>
          ))}

          {/*
            추가 버튼을 목록 끝에 둔다. 띄우는 버튼(FAB)은 마지막 항목을 가려서,
            일정이 꽉 찬 날일수록 방해가 된다.
          */}
          {/* 끝: 이 날 묵는 숙소 — 없으면 정하는 버튼(날짜 설정 시트) */}
          {!reorder && lodgingEnd && <LegRow info={legs.get(lodgingEnd.id)} />}
          {!reorder && (
            <button
              className={`tl-lodging tl-lodging--btn${day.lodgingName ? '' : ' tl-lodging--add'}`}
              onClick={() => setDayEditOpen(true)}
            >
              <span aria-hidden>🏨</span>
              <span className="tl-lodging__text">
                {day.lodgingName ? t.trip.lodgingAt(day.lodgingName) : t.trip.addLodging}
              </span>
            </button>
          )}

          {!reorder && (
            <button
              className="tl-add"
              onClick={() => navigate(`/trip/${trip.id}/item/new?date=${activeDate}`)}
            >
              <PlusIcon size={16} /> {t.trip.addItem}
            </button>
          )}
        </div>
      </main>

      <MenuSheet open={menuOpen} onClose={() => setMenuOpen(false)}>
        <button
          className="sheet__item"
          onClick={() => {
            setMenuOpen(false);
            navigate(`/trip/${trip.id}/checklist`);
          }}
        >
          <ListIcon />
          {t.trip.menuChecklist}
        </button>
        <button
          className="sheet__item"
          onClick={() => {
            setMenuOpen(false);
            navigate(`/trip/${trip.id}/expenses?date=${activeDate}`);
          }}
        >
          <WalletIcon />
          {t.trip.menuLedger}
        </button>
        <button
          className="sheet__item"
          onClick={() => {
            setMenuOpen(false);
            navigate(`/trip/${trip.id}/ideas?date=${activeDate}`);
          }}
        >
          <IdeaIcon />
          {t.trip.menuIdeas}
          {ideaCount > 0 && <span className="sheet__badge">{ideaCount}</span>}
        </button>
        {items.length > 1 && (
          <button
            className="sheet__item"
            onClick={() => {
              setMenuOpen(false);
              setReorder(true);
            }}
          >
            <ReorderIcon />
            {t.trip.menuReorder}
          </button>
        )}
        {/* 옮길 수 있는 방문지가 둘 이상일 때만 의미가 있다 (첫 일정은 고정) */}
        {/* 숙소에서 출발하면 첫 일정도 옮길 수 있다 (domain/optimize.ts) */}
        {items.filter((i, idx) => (idx > 0 || lodgingStart) && i.kind === 'place' && i.coord).length > 1 && (
          <button
            className="sheet__item"
            onClick={() => {
              setMenuOpen(false);
              setOptimizeOpen(true);
            }}
          >
            <RouteIcon />
            {t.trip.menuOptimize}
          </button>
        )}
        {/* 파일 저장은 웹에서만(앱은 아직 플러그인이 없다) */}
        {platform.saveFile && (
          <button
            className="sheet__item"
            onClick={() => {
              setMenuOpen(false);
              const ics = buildIcs(
                trip,
                allItems.filter((i) => i.tripId === trip.id).sort((a, b) =>
                  a.date === b.date ? (a.sortKey < b.sortKey ? -1 : 1) : a.date < b.date ? -1 : 1,
                ),
                { bookingLabel: t.itemEdit.bookingRef },
              );
              /*
               * 파일 이름은 영문으로 — 한글 이름은 일부 브라우저가 버리고 'download'로
               * 저장한다(크롬 헤드리스에서 확인). 여행 이름은 파일 안의 캘린더 이름으로 간다.
               */
              platform.saveFile!(
                `traveltomodachi-${trip.startDate}.ics`,
                ics,
                'text/calendar;charset=utf-8',
              );
            }}
          >
            <CalendarIcon />
            {t.trip.menuCalendar}
          </button>
        )}
        <button className="sheet__item" onClick={() => void onShareDay()}>
          <ChatIcon />
          {t.trip.menuShareDay}
        </button>
        <button className="sheet__item" onClick={onShare}>
          <ShareIcon />
          {t.trip.menuShare}
        </button>
        <button
          className="sheet__item"
          onClick={() => {
            setMenuOpen(false);
            navigate(`/trip/${trip.id}/activity`);
          }}
        >
          <ClockIcon size={19} />
          {t.trip.menuActivity}
        </button>
        <button
          className="sheet__item"
          onClick={() => {
            setMenuOpen(false);
            navigate(`/trip/${trip.id}/members`);
          }}
        >
          <UsersIcon />
          {t.trip.menuMembers}
        </button>
        {/* 쓰다가 막혔을 때 — 홈 왼쪽 위 ?와 같은 곳 */}
        <button
          className="sheet__item"
          onClick={() => {
            setMenuOpen(false);
            navigate('/guide');
          }}
        >
          <HelpIcon size={19} />
          {t.guide.title}
        </button>

        <div className="sheet__sep" />
        {/*
          소유자는 나갈 수 없다(주인 없는 여행이 남는다). 대신 지운다.
          멤버는 지울 수 없다. 남이 만든 여행을 통째로 날리면 안 된다.
        */}
        {isOwner ? (
          <button
            className="sheet__item sheet__item--danger"
            onClick={() => {
              setMenuOpen(false);
              setConfirm('delete');
            }}
          >
            <TrashIcon />
            {t.trip.menuDelete}
          </button>
        ) : (
          <button
            className="sheet__item sheet__item--danger"
            onClick={() => {
              setMenuOpen(false);
              setConfirm('leave');
            }}
          >
            <LeaveIcon />
            {t.trip.menuLeave}
          </button>
        )}

        {/* 라이선스 의무라 메뉴에 상시 노출한다 — DataCredits 주석 참고 */}
        <DataCredits />
      </MenuSheet>

      {confirm === 'delete' && (
        <ConfirmSheet
          title={t.trip.deleteTitle}
          confirmLabel={t.trip.deleteConfirm}
          danger
          onClose={() => setConfirm(null)}
          onConfirm={async () => {
            await deleteTrip(trip.id);
            navigate('/', { replace: true });
          }}
        >
          <p>
            <Rich text={t.trip.deleteBody(trip.name)} />
          </p>
          {others > 0 && <p>{t.trip.deleteOthers(others)}</p>}
        </ConfirmSheet>
      )}

      {confirm === 'leave' && (
        <ConfirmSheet
          title={t.trip.leaveTitle}
          confirmLabel={t.trip.leaveConfirm}
          danger
          onClose={() => setConfirm(null)}
          onConfirm={async () => {
            await leaveTrip(trip.id);
            navigate('/', { replace: true });
          }}
        >
          <p>
            <Rich text={t.trip.leaveBody(trip.name)} />
          </p>
          <p>{t.trip.leaveRejoin}</p>
        </ConfirmSheet>
      )}

      {/* 열 때마다 새로 만든다 — 지난번에 고치다 만 값이 남지 않게 */}
      {dayEditOpen && (
        <DayEditSheet
          key={activeDate}
          tripId={trip.id}
          days={trip.days}
          date={activeDate}
          near={items.flatMap((i) => (i.coord ? [i.coord] : []))}
          onClose={() => setDayEditOpen(false)}
        />
      )}

      {optimizeOpen && (
        <OptimizeSheet
          items={items}
          start={lodgingStart?.coord}
          end={lodgingEnd?.coord}
          onClose={() => setOptimizeOpen(false)}
          onApply={(order) => {
            setDayOrder(trip.id, activeDate, order);
            setOptimizeOpen(false);
            platform.vibrate(8);
          }}
        />
      )}

      {(toast ?? dayToast) && <div className="toast">{toast ?? dayToast}</div>}

      <BottomTabs tripId={trip.id} date={activeDate} />
    </div>
  );
}
